import React, { useState } from 'react'
import { Formik } from 'formik'
import { Link } from 'gatsby'

import { breakpoints, useDimensions } from 'src/ui/dimensions'
import { Body, Header } from 'src/ui/typography'
import { useTheme, styled } from 'src/ui/theme'
import { HBox } from 'src/ui/layout'
import { Input, InputWithUpload, Button, Select } from 'src/ui/molecules'
import { MaskedInput } from 'src/ui/organisms'
import { formValidationSchema } from './validator'
import { TDefaultReferralFormValues } from './types'
import { usePostLead } from 'src/entities/leads/hooks'
import { sendAnalyticsEvent } from 'src/shared/features/analytics'
import { addMessage } from 'src/shared/features/snack'
import { upload } from 'src/shared/upload/upload'

const defaultValues: TDefaultReferralFormValues = {
  name: '',
  phone: '',
  email: '',
  telegram: '',
  vacancy: '',
  friendPhone: '',
  resume: '',
  message: '',
}

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.gray.bg};

  @media (max-width: ${breakpoints.desktop}px) {
    padding-left: 64px;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    padding: 0 16px;
  }
`

const Form = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  margin-left: calc((100% - 1312px) / 2);
  padding-top: 80px;

  @media (max-width: ${breakpoints.desktop}px) {
    margin-left: 0;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    padding: 0;
    margin: auto;
  }
`

const StyledTitle = styled(Header)`
  color: ${({ theme }) => theme.colors.black.primary};

  @media (max-width: ${breakpoints.mobile}px) {
    margin-top: 24px;
  }
`

const SelfFormBlock = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 135px;
  row-gap: 14px;

  @media (max-width: ${breakpoints.mobile}px) {
    display: flex;
    flex-direction: column;
    row-gap: 7px;
  }
`

const FriendAboutFormBlock = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 135px;

  @media (max-width: ${breakpoints.mobile}px) {
    display: flex;
    flex-direction: column;
    row-gap: 28px;
  }
`

const LeftSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const StyledButton = styled(Button)`
  width: 260px;

  @media (max-width: ${breakpoints.mobile}px) {
    width: 216px;
  }
`

const Notice = styled(Body)`
  opacity: 0.5;
  color: ${({ theme }) => theme.colors.gray.primary};
`

type Props = {
  vacancies: null | TOption[]
}

export const ReferralForm = ({ vacancies }: Props) => {
  const theme = useTheme()
  const { isMobile } = useDimensions()

  const [addLead] = usePostLead();

  const [isLoading, setIsLoading] = useState(false)
  const [resume, setResume] = React.useState<File | null>(null)

  const handleChangeName = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined
    ) => void,
    values: TDefaultReferralFormValues
  ) => {
    const value = e.target.value
    const clearValue = value.replace(/\d/g, '')

    if (clearValue !== values.name) {
      setFieldValue('name', clearValue)
    }
  }

  return (
    <Wrapper>
      <Formik
        initialValues={defaultValues}
        onSubmit={(values, { resetForm }) => {
          setIsLoading(true)

          const sendForm = (resumeId?: string) => {
            const firstName = values.name.split(' ')[0]
            const secondName = values.name.split(' ')[1] ?? ''
            const bio = `МОЙ ТЕЛЕГРАМ: ${
              values.telegram
            }, ВАКАНСИЯ ДЛЯ ДРУГА: ${values.vacancy}, ТЕЛЕФОННЫЙ НОМЕР ДРУГА: ${
              values.friendPhone
            }, ДОПОЛНИТЕЛЬНОЕ СООБЩЕНИЕ: ${values.message ?? ''}`

            addLead({
              variables: {
                data: {
                  firstName: firstName,
                  secondName: secondName,
                  email: values.email,
                  phone: values.phone,
                  resumeLink: values.resume,
                  bio: bio,
                  type: 'Рекомендация друга',
                  resume: resumeId ?? '',
                },
              },
            })
              .then(() => {
                addMessage({
                  text: 'Мы уже читаем ваше сообщение ;)',
                  id: 'vacancyFormSuccess',
                })
                // sendAnalyticsEvent('RESPOND_VACANCY_FINISH') // TODO обсудить с PM куда шлем аналитику
                resetForm()
                setResume(null)
                setIsLoading(false)
              })
              .catch(() => {
                addMessage({
                  text: 'Что-то пошло не так. Попытайтесь позднее',
                  id: 'vacancyFormError',
                })
                setIsLoading(false)
                // sendAnalyticsEvent('RESPOND_VACANCY_FAILURE') // TODO обсудить с PM куда шлем аналитику
              })
          }

          const uploads = [
            {
              file: resume,
              prefix: 'resume',
            },
          ].filter((file) => file.file !== null)

          //@ts-ignore
          const uploadRes = upload(uploads)
          if (uploadRes) {
            uploadRes
              //@ts-ignore
              .then((response: Response) => response.json())
              .then((data?: { id: string; name: string }[]) => {
                const resumeId = data
                  ? data.find((file) => file.name.indexOf('resume_') !== -1)?.id
                  : ''

                sendForm(resumeId)
              })
          } else {
            sendForm('')
          }
        }}
        validationSchema={formValidationSchema}
        validateOnBlur
      >
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          values,
          errors,
          touched,
        }) => (
          <Form>
            <StyledTitle size={isMobile ? 'h4' : 'h2'}>
              Заработай до 100 тысяч рублей* и помоги другу присоединиться к
              команде KODE!
            </StyledTitle>

            <HBox height={isMobile ? 48 : 64} />

            <Header
              color={theme.colors.black.primary}
              size={isMobile ? 'h5' : 'h4'}
            >
              Представься:
            </Header>
            <HBox height={isMobile ? 20 : 24} />
            <SelfFormBlock>
              <Input
                value={values.name || ''}
                onBlur={handleBlur}
                onChange={(e) => handleChangeName(e, setFieldValue, values)}
                name="name"
                title="Имя и Фамилия"
                placeholder="Дэн Абрамов"
                error={touched.name && errors.name}
                disabled={isLoading}
              />
              <Input
                value={values.email || ''}
                onBlur={handleBlur}
                onChange={handleChange}
                name="email"
                title="Email"
                placeholder="youremail@email.ru"
                type="email"
                error={touched.email && errors.email}
                disabled={isLoading}
              />
              <MaskedInput
                value={values.phone || ''}
                mask="+7 999 999 99 99"
                onBlur={handleBlur}
                onChange={handleChange}
                name="phone"
                title="Телефон"
                placeholder="8 (900) 000 00-00"
                type="tel"
                maxLength={16}
                error={touched.phone && errors.phone}
                disabled={isLoading}
              />
              <Input
                value={values.telegram || ''}
                onBlur={handleBlur}
                onChange={handleChange}
                name="telegram"
                title="Telegram"
                placeholder="@yourtelegram"
                error={touched.telegram && errors.telegram}
                disabled={isLoading}
              />
            </SelfFormBlock>

            <HBox height={isMobile ? 40 : 64} />

            <Header
              color={theme.colors.black.primary}
              size={isMobile ? 'h5' : 'h4'}
            >
              Поделись контактом друга:
            </Header>
            <HBox height={24} />
            <FriendAboutFormBlock>
              <LeftSide>
                {vacancies && (
                  <Select
                    value={values.vacancy}
                    onBlur={handleBlur}
                    name="vacancy"
                    onChange={(e) => setFieldValue('vacancy', e.label)}
                    options={vacancies}
                    caption="Вакансия"
                    placeholder="Выберите вакансию"
                    error={touched.vacancy && errors.vacancy}
                  />
                )}
                <MaskedInput
                  value={values.friendPhone || ''}
                  mask="+7 999 999 99 99"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  name="friendPhone"
                  title="Телефон"
                  placeholder="8 (900) 000 00-00"
                  type="tel"
                  maxLength={16}
                  error={touched.friendPhone && errors.friendPhone}
                  disabled={isLoading}
                />
                <InputWithUpload
                  textName="resume"
                  textValue={values.resume || ''}
                  fileName="resume"
                  onTextChange={handleChange}
                  caption="Резюме (опционально)"
                  file={resume}
                  placeholder={
                    isMobile
                      ? 'Ссылка или PDF'
                      : 'Ссылка на резюме или добавьте файл pdf'
                  }
                  onFileChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      setResume(e.target.files[0])
                      handleChange(e)
                    }
                  }}
                  onRemoveFile={() => {
                    setResume(null)
                    setFieldValue('resume', null)
                  }}
                  accept={['.doc', '.docx', '.pdf', '.txt']}
                  disabled={isLoading}
                />
              </LeftSide>
              <Input
                value={values.message || ''}
                title="Сообщение (опционально)"
                name="message"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="Если есть необходимость что-то рассказать"
                multiline
                multilineHeight={isMobile ? '192px' : '296px'}
                error={touched.message && errors.message}
              />
            </FriendAboutFormBlock>

            <HBox height={isMobile ? 24 : 80} />

            <StyledButton
              variant="primary"
              onClick={() => handleSubmit()}
              disabled={isLoading}
            >
              Рекомендовать друга
            </StyledButton>

            <HBox height={16} />

            <Body color={theme.colors.black.primary} linkUnderline>
              Нажимая на кнопку «Рекомендовать друга», ты принимаешь&nbsp;
              <a href="#">
                <Link to="referral/referral-policy">условия соглашения</Link>
              </a>
            </Body>

            <HBox height={16} />

            <Notice>* До вычета НДФЛ</Notice>

            <HBox height={isMobile ? 40 : 80} />
          </Form>
        )}
      </Formik>
    </Wrapper>
  )
}
